import HistoricoSection from '@atendimento/detail/historico/components/HistoricoSection'
import HistoricoDetailTable from '@atendimento/detail/historico/components/table/HistoricoDetailTable'
import { historicoSubsectionStyles } from '@atendimento/detail/historico/model/model-historico'
import {
  convertFatoToEncaminhamentoEspecializado,
  convertFatoToGuiaEncaminhamento,
  convertFatoToReceitaMedicamento,
  naoRegistradoNesseAtendimento,
} from '@atendimento/detail/historico/utils/utils-historico'
import { convertFatoSolicitacaoOciToSolicitacaoOciModel } from '@atendimento/detail/soap/plano/exames/utils-oci'
import { EMPTY_ARRAY, Flow, Heading, isEmpty, Text } from 'bold-ui'
import {
  Ciap,
  DimensaoProcedimento,
  FatoAtendimentoIndividual,
  FatoAtendimentoIndividualMedicamentos,
  FichasConcatenadasHistorico,
} from 'graphql/types.generated'

import { HistoricoExamesSolicitadosDwTable } from '../../../HistoricoExamesSolicitadosDwTable'
import HistoricoPlanoPecEncaminhamentosEspecializadosPanel from '../../consulta/soap/plano/HistoricoPlanoPecEncaminhamentosEspecializadosPanel'
import HistoricoPlanoPecGuiaEncaminhamentoList from '../../consulta/soap/plano/HistoricoPlanoPecGuiaEncaminhamentoList'
import HistoricoPlanoPecPrescricaoPanel from '../../consulta/soap/plano/HistoricoPlanoPecPrescricaoPanel'
import HistoricoPlanoSolicitacoesOciPanel from '../../consulta/soap/plano/HistoricoPlanoSolicitacoesOciPanel'
import { isFiltroPreenchido } from '../utils-HistoricoDwCiapFiltro'
import { HistoricoCiapProcedimento } from './HistoricoCiapProcedimento'

type HistoricoDwPlanoProps = Pick<
  FatoAtendimentoIndividual,
  | 'examesSolicitados'
  | 'solicitacoesOci'
  | 'filtroCiapPlano'
  | 'fatoAppAtendimento'
  | 'medicamentos'
  | 'encaminhamentosEspecializados'
  | 'encaminhamentoServicoEspecializado'
>

interface HistoricoDwPlanoSectionProps {
  historico: HistoricoDwPlanoProps
  ciap2Filtro: Ciap[]
  procedimentosSisab: DimensaoProcedimento[] | null
  fichasConcatenadas?: FichasConcatenadasHistorico
}

export const HistoricoDwPlanoSection = (props: Readonly<HistoricoDwPlanoSectionProps>) => {
  const { historico, ciap2Filtro, procedimentosSisab, fichasConcatenadas } = props

  const hasMedicamentos = !isEmpty(historico.medicamentos)
  const hasCampoAbertoPlano = !isEmpty(historico.fatoAppAtendimento?.campoAbertoPlano)
  const hasExamesSolicitados = !isEmpty(historico.examesSolicitados)

  const encaminhamentosAntigos = historico.encaminhamentosEspecializados?.filter(
    (enc) => enc?.especialidadeSisreg && !enc?.dimensaoCboGuiaEncaminhamento
  )
  const encaminhamentosGuia = historico.encaminhamentosEspecializados?.filter(
    (enc) => enc?.dimensaoCboGuiaEncaminhamento
  )

  const hasEncaminhamentosAntigos = !isEmpty(encaminhamentosAntigos)
  const hasEncaminhamentosGuia = !isEmpty(encaminhamentosGuia)

  const hasPlano =
    hasCampoAbertoPlano ||
    hasExamesSolicitados ||
    !isEmpty(historico.solicitacoesOci) ||
    !isEmpty(procedimentosSisab) ||
    fichasConcatenadas?.uuidProcedimento ||
    hasMedicamentos ||
    isFiltroPreenchido(historico.filtroCiapPlano) ||
    hasEncaminhamentosAntigos ||
    hasEncaminhamentosGuia

  return (
    <HistoricoSection section='P'>
      {hasPlano ? (
        <Flow direction='vertical' gap={0.5}>
          {hasCampoAbertoPlano && <Text>{historico.fatoAppAtendimento?.campoAbertoPlano}</Text>}
          {hasExamesSolicitados && (
            <HistoricoExamesSolicitadosDwTable exames={historico.examesSolicitados ?? EMPTY_ARRAY} />
          )}
          {!isEmpty(historico.solicitacoesOci) && historico.solicitacoesOci && (
            <Flow direction='vertical' gap={0.5}>
              <Heading level={5} style={historicoSubsectionStyles.plano}>
                Ofertas de Cuidados Integrados solicitadas
              </Heading>
              <HistoricoPlanoSolicitacoesOciPanel
                solicitacoesOci={convertFatoSolicitacaoOciToSolicitacaoOciModel(historico.solicitacoesOci)}
              />
            </Flow>
          )}
          {(!isEmpty(procedimentosSisab) || isFiltroPreenchido(historico.filtroCiapPlano)) && (
            <Flow direction='vertical' gap={0.5}>
              <Heading level={5} style={historicoSubsectionStyles.plano}>
                Intervenções e/ou procedimentos clínicos
              </Heading>
              <HistoricoDetailTable hasLines>
                <HistoricoCiapProcedimento
                  filtro={historico.filtroCiapPlano}
                  ciaps={ciap2Filtro}
                  showLabel={true}
                  procedimentosSisab={procedimentosSisab}
                />
              </HistoricoDetailTable>
            </Flow>
          )}
          {hasMedicamentos && (
            <Flow direction='vertical' gap={0.5}>
              <Heading style={historicoSubsectionStyles.plano} level={5}>
                Medicamentos prescritos
              </Heading>
              <HistoricoPlanoPecPrescricaoPanel
                prescricaoMedicamentos={historico.medicamentos?.map((medicamento) =>
                  convertFatoToReceitaMedicamento(medicamento as FatoAtendimentoIndividualMedicamentos)
                )}
              />
            </Flow>
          )}
          {(hasEncaminhamentosAntigos || historico.encaminhamentoServicoEspecializado) && (
            <Flow direction='vertical' gap={0.5}>
              <Heading level={5} style={historicoSubsectionStyles.plano}>
                Encaminhamentos
              </Heading>
              <Text>Serviço especializado</Text>
              {hasEncaminhamentosAntigos && (
                <HistoricoPlanoPecEncaminhamentosEspecializadosPanel
                  encaminhamentos={
                    encaminhamentosAntigos?.map((enc) => convertFatoToEncaminhamentoEspecializado(enc)) ?? []
                  }
                />
              )}
            </Flow>
          )}
          {hasEncaminhamentosGuia && (
            <Flow direction='vertical' gap={0.5} css={{ width: '100%' }}>
              <Heading level={5} style={historicoSubsectionStyles.plano}>
                Encaminhamento/Compartilhamento de cuidado
              </Heading>
              <HistoricoPlanoPecGuiaEncaminhamentoList
                guiasEncaminhamento={encaminhamentosGuia?.map((enc) => convertFatoToGuiaEncaminhamento(enc)) ?? []}
                showOnlyDwFields
              />
            </Flow>
          )}
        </Flow>
      ) : (
        <Text fontStyle='italic'>{naoRegistradoNesseAtendimento}</Text>
      )}
    </HistoricoSection>
  )
}
